import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { isAxiosError } from "axios";
import { companiesApi, orderRecommendationApi, schemesApi, portfolioLiqRiskAPI } from "@/services/api";
import { PortfolioLiquidityRiskItem } from "@/types/client";
import { isGlobalCountryCode } from "@/utils/country";
import {
  PendingOrder,
  StrategyFilter,
  BulkEditFields,
  BulkCreateFields,
  ConfirmDialogState,
  SnackbarState,
} from "../types";
import {
  buildRiskLookup,
  CORE_DOMESTIC_STRATEGY,
  type RiskByCompanyId,
} from "../riskViolations";
import {
  LHF_CODES,
  defaultStartDate,
  defaultCategoriesFor,
  matchesFilters,
  calcDefaultPrice,
  type OrderTypeFilter,
  type PriorityFilter,
  type MethodFilter,
  type DecisionFilter,
  type LastOrderFilter,
  type MarketableFilter,
  type RowTypeFilter,
  type FilterLevel,
  anyOrderLevelFilterActive,
  matchesExecutionFilters,
  matchesLastOrderFilter,
} from "../utils";
import { hasViewAtHorizon, findMatchedRec, hasActionableSuggestion } from "../recommendationSelectors";

// One page wide enough for the whole company master: the note map has to cover every
// company a ticket can name, and the list endpoint pages at 20 by default.
const COMPANY_NOTE_PAGE_SIZE = 5000;

// Decision sort priority: continue → completed → none.
const decisionRank = (d: string | null | undefined): number => {
  if (d === "continue") return 0;
  if (d === "completed") return 1;
  return 2;
};

// Country tab split. India ("IN") is Domestic; every other code is Global.
// A null/unknown country_code is treated as Domestic so legacy Indian companies
// with an unset country don't disappear into Global (mirrors trade_suggestions).
export type CountryTab = "domestic" | "global";

/** Outcome of the Domestic sign-off. The last two both leave the desk unmessaged but read
 *  very differently to the user, so they stay distinct. */
export type MarkCompleteResult = "notified" | "nothing-to-send" | "error";
const isGlobalRow = (o: PendingOrder): boolean =>
  isGlobalCountryCode(o.country_code);

// A recommendation whose raw currency (recommendation prices) differs from the LTP
// currency (CMP) can't yield a valid BUY/SELL — the suggestion compares prices across
// currencies. Such rows are dropped from the suggestion-filtered recommendations list.
const hasCurrencyMismatch = (o: PendingOrder): boolean =>
  o.currency_code != null &&
  o.ltp_currency_code != null &&
  o.currency_code !== o.ltp_currency_code;

/**
 * State scoped per country tab: Domestic and Global each keep their own value,
 * so switching tabs restores that tab's selection instead of sharing one.
 * Returns the active tab's value plus a setter (supports functional updates)
 * that only mutates the active tab's slice — a drop-in for `useState`.
 */
function useTabScopedState<T>(
  tab: CountryTab,
  initial: T | ((tab: CountryTab) => T),
): [T, Dispatch<SetStateAction<T>>] {
  const [byTab, setByTab] = useState<Record<CountryTab, T>>(() => {
    // A function initial gives each tab its own starting value (e.g. Global's wider
    // default categories); a plain value starts both tabs the same.
    if (typeof initial !== "function") return { domestic: initial, global: initial };
    const initialFor = initial as (tab: CountryTab) => T;
    return { domestic: initialFor("domestic"), global: initialFor("global") };
  });
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      setByTab((prev) => {
        const next =
          typeof action === "function"
            ? (action as (p: T) => T)(prev[tab])
            : action;
        return { ...prev, [tab]: next };
      });
    },
    [tab],
  );
  return [byTab[tab], setValue];
}

export const useOrderRecommendations = () => {
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // recStartDate drives the (single) data fetch, so it stays global across tabs.
  const [recStartDate, setRecStartDate] = useState(defaultStartDate());
  // The active country tab. Declared before the tab-scoped filters below, which
  // key their per-tab slice off it so each tab remembers its own filter state.
  const [countryTab, setCountryTab] = useState<CountryTab>("domestic");
  const [strategyFilter, setStrategyFilter] = useTabScopedState<StrategyFilter>(countryTab, "all");
  // Global starts with W1/W2/W3 selected on top of the shared defaults; each tab keeps
  // its own selection, so switching tabs restores that tab's chips.
  const [selectedCategories, setSelectedCategories] = useTabScopedState<string[]>(
    countryTab,
    (tab) => defaultCategoriesFor(tab === "global"),
  );
  const defaultCategories = defaultCategoriesFor(countryTab === "global");
  const [searchTerm, setSearchTerm] = useTabScopedState<string>(countryTab, "");
  const [orderTypeFilter, setOrderTypeFilter] = useTabScopedState<OrderTypeFilter>(countryTab, "all");
  const [priorityFilter, setPriorityFilter] = useTabScopedState<PriorityFilter>(countryTab, "all");
  const [methodFilter, setMethodFilter] = useTabScopedState<MethodFilter>(countryTab, "all");
  const [decisionFilter, setDecisionFilter] = useTabScopedState<DecisionFilter>(countryTab, "all");
  const [lastOrderFilter, setLastOrderFilter] = useTabScopedState<LastOrderFilter>(countryTab, "all");
  const [marketableFilter, setMarketableFilter] = useTabScopedState<MarketableFilter>(countryTab, "all");
  const [rowTypeFilter, setRowTypeFilter] = useTabScopedState<RowTypeFilter>(countryTab, "all");
  const [filterLevel, setFilterLevel] = useTabScopedState<FilterLevel>(countryTab, "company");
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkEditState, setBulkEditState] = useState<Record<number, BulkEditFields>>({});
  const [bulkEditSnapshot, setBulkEditSnapshot] = useState<Record<number, BulkEditFields>>({});
  // state + snapshot are kept in one useState so a create-row edit updates both
  // atomically in a single render — two independent useState calls would risk a
  // render observing one updated without the other.
  const [bulkCreatePair, setBulkCreatePair] = useState<{
    state: Record<string, BulkCreateFields>;
    snapshot: Record<string, BulkCreateFields>;
  }>({ state: {}, snapshot: {} });
  const bulkCreateState = bulkCreatePair.state;
  const bulkCreateSnapshot = bulkCreatePair.snapshot;
  // Create-row keys (`${company}_${strategyGroup}`) already persisted in this edit
  // session via a partial save. Their drafts stay visible so the row keeps showing
  // the placed buy/sell, but they are excluded from later saves so we never create
  // a duplicate order. Reset whenever a fresh edit session begins.
  const savedCreateKeysRef = useRef<Set<string>>(new Set());
  // True once a partial save has persisted rows but we stayed in edit mode (so we
  // skipped the usual post-save refetch). pendingOrders is then stale, so exiting
  // edit mode — via Cancel or a later full save — must refetch to show real data.
  const partiallySavedRef = useRef(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    type: null,
    orderId: null,
    companyName: null,
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });
  // Rows flagged by the last pre-save validation pass, used to highlight them.
  const [saveErrors, setSaveErrors] = useState<{ createKeys: string[]; editIds: number[] }>({
    createKeys: [],
    editIds: [],
  });
  const [sortCol, setSortCol] = useState<"company" | "days" | "decision" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Liquidity-risk limits keyed by company id, used to flag target positions that
  // breach the company's max-permissible position. Computed from the Core-Domestic
  // portfolio; fails soft (indicators just don't render).
  const [riskByCompanyId, setRiskByCompanyId] = useState<RiskByCompanyId>({});
  const [companyIdsMissingLiquidity, setCompanyIdsMissingLiquidity] = useState<number[]>([]);
  // Scheme codes per strategy are stable, so resolve them once and reuse across risk
  // fetches; `schemesReady` gates the risk fetch until they're loaded.
  const strategySchemeCodesRef = useRef<{ core: string[] }>({ core: [] });
  const [schemesReady, setSchemesReady] = useState(false);
  const aifSplitAbortControllerRef = useRef<AbortController | null>(null);

  // The Days column is hidden on the Global tab, so a days sort carried over from
  // Domestic would silently reorder rows with no header to undo it.
  useEffect(() => {
    if (countryTab === "global" && sortCol === "days") setSortCol(null);
  }, [countryTab, sortCol]);

  const toggleSort = useCallback((col: "company" | "days" | "decision") => {
    if (sortCol !== col) {
      setSortCol(col);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  }, [sortCol]);

  // Cleanup effect: Abort the AIF lots age split fetch if the component unmounts
  useEffect(() => {
    return () => {
      if (aifSplitAbortControllerRef.current) {
        aifSplitAbortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchPendingOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderRecommendationApi.getOrders({
        start_date: recStartDate,
      });

      const orders = Array.isArray(data) ? data : [];
      setPendingOrders(orders);
      setError(null);

      // Cancel any previously running fetch
      if (aifSplitAbortControllerRef.current) {
        aifSplitAbortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      aifSplitAbortControllerRef.current = abortController;

      // Asynchronously fetch AIF lots age split without blocking the UI
      const companyIds = Array.from(new Set(orders.map(o => o.company).filter((c): c is number => c != null)));
      if (companyIds.length > 0) {
        orderRecommendationApi.fetchAifLotsAgeSplit(
          companyIds,
          { signal: abortController.signal }
        ).then(splitData => {
          // Guard against the response arriving after being aborted or unmounted
          if (abortController.signal.aborted) return;

          setPendingOrders(prevOrders =>
            prevOrders.map(order => {
              if (order.company == null) return order;
              const split = splitData[order.company];

              if (split) {
                return {
                  ...order,
                  aif_lt_qty: split.lt_qty,
                  aif_st_qty: split.st_qty,
                  oldest_aif_st_lot_date: split.oldest_st_date,
                  oldest_aif_st_lot_age_days: split.oldest_st_age_days,
                };
              }
              return order;
            })
          );
        }).catch((err: unknown) => {
          // Ignore intentional cancellations (abortController.abort() surfaces as an axios CanceledError)
          if (isAxiosError(err) && (err.code === "ERR_CANCELED" || err.name === "CanceledError")) return;
          console.error("Failed to fetch AIF lots age split", err);
        });
      }

      // The CIO note is a company field, so it arrives with the companies rather than
      // from an endpoint of its own. Fetched off the critical path like the AIF split
      // above, and sharing its abort controller — one refetch cancels both.
      if (companyIds.length > 0) {
        companiesApi.getCompanies(
          { page_size: COMPANY_NOTE_PAGE_SIZE },
          { signal: abortController.signal }
        ).then(({ results }) => {
          if (abortController.signal.aborted) return;

          const noteByCompanyId = new Map(
            results.filter(company => company.cio_note).map(company => [company.id, company.cio_note!])
          );
          setPendingOrders(prevOrders =>
            prevOrders.map(order =>
              order.company != null && noteByCompanyId.has(order.company)
                ? { ...order, cio_note: noteByCompanyId.get(order.company) }
                : order
            )
          );
        }).catch((err: unknown) => {
          if (isAxiosError(err) && (err.code === "ERR_CANCELED" || err.name === "CanceledError")) return;
          console.error("Failed to fetch CIO notes", err);
        });
      }
    } catch {
      setError("Failed to fetch pending orders");
    } finally {
      setLoading(false);
    }
  }, [recStartDate]);

  useEffect(() => {
    fetchPendingOrders();
  }, [fetchPendingOrders]);

  // Sorted, deduped company ids across the order-rec list. This is the set the
  // risk endpoint must cover — including not-yet-held companies (new buy recs) — so
  // their targets can be checked too. Stable string key → risk fetch only re-runs when
  // the set of companies actually changes.
  // Non-Indian (Global) companies are excluded: liquidity risk is a Core-Domestic
  // concept, so they'd otherwise be requested, come back empty, and be wrongly
  // flagged as "liquidity data unavailable".
  const orderCompanyIdsKey = useMemo(() => {
    const ids = Array.from(
      new Set(
        pendingOrders
          .filter((o) => !isGlobalRow(o))
          .map((o) => o.company)
          .filter((c): c is number => c != null),
      ),
    ).sort((a, b) => a - b);
    return ids.join(",");
  }, [pendingOrders]);

  // Resolve the Core-Domestic scheme codes once (they don't change per session).
  useEffect(() => {
    let cancelled = false;
    const loadStrategySchemeCodes = async () => {
      try {
        const schemesResp = await schemesApi.getSchemes({ page_size: 1000, is_active: true });
        const schemeCodesForStrategy = (strategyName: string): string[] =>
          schemesResp.results
            .filter((s) => s.investment_strategy_display === strategyName && s.code)
            .map((s) => s.code as string);
        strategySchemeCodesRef.current = {
          core: schemeCodesForStrategy(CORE_DOMESTIC_STRATEGY),
        };
      } catch {
        // Fail soft: leave scheme codes empty so the risk fetch simply no-ops.
        strategySchemeCodesRef.current = { core: [] };
      } finally {
        if (!cancelled) setSchemesReady(true);
      }
    };
    void loadStrategySchemeCodes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the Core portfolio's liquidity limits for exactly the companies in the order
  // list and merge into a per-symbol lookup. Re-runs when the company set changes.
  // Fails soft — on error the target cells show no risk indicators.
  useEffect(() => {
    if (!schemesReady || !orderCompanyIdsKey) return;
    let cancelled = false;

    const fetchRisk = (schemeCodes: string[]): Promise<PortfolioLiquidityRiskItem[]> =>
      schemeCodes.length > 0
        ? portfolioLiqRiskAPI
            .getPortfolioLiquidityRisk({ scheme_codes: schemeCodes.join(","), company_ids: orderCompanyIdsKey })
            .then((res) => res.data ?? [])
        : Promise.resolve([]);

    const loadRiskLimits = async () => {
      try {
        const { core } = strategySchemeCodesRef.current;
        const coreItems = await fetchRisk(core);
        if (cancelled) return;
        const lookup = buildRiskLookup(coreItems);
        setRiskByCompanyId(lookup.riskByCompanyId);
        setCompanyIdsMissingLiquidity(lookup.missingCompanyIds);
      } catch {
        if (cancelled) return;
        setRiskByCompanyId({});
        setCompanyIdsMissingLiquidity([]);
      }
    };

    void loadRiskLimits();
    return () => {
      cancelled = true;
    };
  }, [schemesReady, orderCompanyIdsKey]);

  // Held companies (present in the current orders) whose liquidity data is missing,
  // so their target positions couldn't be checked — surfaced as a disclaimer.
  // NOTE: this only covers companies the risk API RETURNED but with no median value
  // (buildRiskLookup's missingCompanyIds). Companies the API omitted from the response
  // entirely are not in missingCompanyIds, so they get no warning icon AND are not
  // counted here — the disclaimer therefore under-reports total missing coverage.
  const companiesMissingLiquidity = useMemo(() => {
    if (companyIdsMissingLiquidity.length === 0) return [];
    const missing = new Set(companyIdsMissingLiquidity);
    const nameByCompanyId = new Map<number, string>();
    for (const order of pendingOrders) {
      // Global companies aren't risk-checked, so never warn about their liquidity.
      if (isGlobalRow(order)) continue;
      if (missing.has(order.company)) {
        nameByCompanyId.set(order.company, order.company_name || order.company_symbol);
      }
    }
    return Array.from(nameByCompanyId.values());
  }, [pendingOrders, companyIdsMissingLiquidity]);

  const groupedByCompany = useMemo(() => {
    // "Recommendations only" hides all pending-order groups.
    if (rowTypeFilter === "recommendations") return [];

    // 1. Build map from real trackers
    const allTrackers = pendingOrders.filter((o) => !o.is_oms_rec);
    const fullMap = new Map<number, PendingOrder[]>();
    for (const order of allTrackers) {
      if (!fullMap.has(order.company)) fullMap.set(order.company, []);
      fullMap.get(order.company)!.push(order);
    }

    // 2. Inject horizon-indicator rows for missing strategy groups.
    // These are IS_OMS_REC rows within a pending order group — rendered as
    // dashed-border indicator rows showing model IR but no editable fields.
    for (const [cid, orders] of fullMap.entries()) {
      const rep = orders[0];
      const hasLhf = orders.some((o) => o.strategy_group === "LHF" || LHF_CODES.has(o.strategy_code ?? ""));
      const hasCore = orders.some((o) => !(o.strategy_group === "LHF" || LHF_CODES.has(o.strategy_code ?? "")));
      const base: PendingOrder = {
        ...rep, strategy: null, strategy_code: null,
        schemes: [], limit_price: "", is_completed: false,
        cio_decision: null, target_holding_pct: null, cio_target_pct: null, comment: null,
        is_oms_rec: false,
        is_placeholder: true,
        last_order_mismatch: false,
        last_orders_by_strategy: {},
        has_session_order: false,
        pending_order_codes: [],
      };
      // Inject a synthetic indicator row only when some author — model, analyst or IC —
      // holds a view at the missing horizon. `...rep` carries the raw `recommendations`
      // array forward so TableRow re-derives the IR + decision on render; an analyst-only
      // five-year view renders with its Analyst / IC cells filled and the Model ones blank.
      if (hasCore && !hasLhf && hasViewAtHorizon(rep.recommendations, 5)) {
        fullMap.get(cid)!.push({
          ...base, id: -(cid + 10_000_000), strategy_group: "LHF",
        });
      }
      if (hasLhf && !hasCore && hasViewAtHorizon(rep.recommendations, 3)) {
        fullMap.get(cid)!.push({
          ...base, id: -(cid + 20_000_000), strategy_group: "CORE",
        });
      }
    }

    // 3. Filter the resulting groups
    const finalGroups: PendingOrder[][] = [];
    const anyExecutionFilterActive = anyOrderLevelFilterActive(
      orderTypeFilter, priorityFilter, methodFilter, decisionFilter, lastOrderFilter, marketableFilter
    );

    for (const rows of fullMap.values()) {
      let filteredRows: PendingOrder[];

      if (filterLevel === "company") {
        // Step 1: apply base filters (strategy, search, category) per row.
        const baseRows = rows.filter((o) =>
          matchesFilters(o, strategyFilter, searchTerm, selectedCategories)
        );

        // Step 2: check if ANY real row passes all execution filters.
        const anyRealMatch = baseRows.some((o) =>
          !o.is_placeholder && !o.is_oms_rec &&
          matchesExecutionFilters(o, orderTypeFilter, priorityFilter, methodFilter, decisionFilter, marketableFilter) &&
          matchesLastOrderFilter(o, lastOrderFilter)
        );

        if (!anyRealMatch) continue;
        filteredRows = baseRows;
      } else {
        // Strategy level: each row filtered independently. Placeholders are also
        // dropped when any execution filter is active, so they don't bypass the filter.
        filteredRows = rows.filter((o) => {
          if (!matchesFilters(o, strategyFilter, searchTerm, selectedCategories, orderTypeFilter, priorityFilter, methodFilter, decisionFilter, lastOrderFilter, marketableFilter)) {
            return false;
          }
          if (o.is_placeholder && anyExecutionFilterActive) return false;
          return true;
        });
      }

      // Drop the whole company group if only orphaned placeholder rows remain.
      const hasRealOrder = filteredRows.some((o) => !o.is_placeholder);
      if (!hasRealOrder) continue;

      if (filteredRows.length > 0) {
        // Sort within group
        filteredRows.sort((a, b) => {
          // LHF always above Core.
          const aLhf = a.strategy_group === "LHF" || LHF_CODES.has(a.strategy_code || "");
          const bLhf = b.strategy_group === "LHF" || LHF_CODES.has(b.strategy_code || "");
          if (aLhf !== bLhf) return aLhf ? -1 : 1;
          return a.order_type - b.order_type;
        });
        finalGroups.push(filteredRows);
      }
    }

    // 4. Sort groups: explicit column → priority bucket (stable within bucket).
    //
    // Bucket 0 — cio_decision == "continue" set today.
    // Bucket 1 — any other cio_decision set today (e.g. completed).
    // Bucket 2 — created today.
    // Bucket 3 — prev-day "continue".
    // Bucket 4 — "Prev": an order was placed on the last working day (has_yesterday_order).
    // Bucket 5 — everything else.
    // NOTE: is_prev_day_continue implies has_yesterday_order (a continued order was placed),
    // so bucket 3 and 4 overlap. The early-return ordering is deliberate — prev-day-continue
    // is the more specific, higher-priority state and must win over a plain prev-day order.
    // Do not reorder these checks.
    const bucketFor = (group: PendingOrder[]): number => {
      if (group.some((o) => o.cio_decision === "continue")) return 0;
      if (group.some((o) => o.cio_decision != null && o.cio_decision !== "continue")) return 1;
      if (group.some((o) => o.is_new_today)) return 2;
      if (group.some((o) => o.is_prev_day_continue)) return 3;
      if (group.some((o) => o.has_yesterday_order)) return 4;
      return 5;
    };

    const groupDecisionRank = (group: PendingOrder[]): number => Math.min(
      ...group.map((o) => decisionRank(o.cio_decision))
    );

    const sorted = finalGroups.sort((a, b) => {
      if (sortCol === "company") {
        const cmp = (a[0].company_name ?? "").localeCompare(b[0].company_name ?? "");
        return sortDir === "asc" ? cmp : -cmp;
      }
      if (sortCol === "days") {
        const da = a[0].days ?? Infinity;
        const db = b[0].days ?? Infinity;
        return sortDir === "asc" ? da - db : db - da;
      }
      if (sortCol === "decision") {
        const dr = groupDecisionRank(a) - groupDecisionRank(b);
        return sortDir === "asc" ? dr : -dr;
      }
      return bucketFor(a) - bucketFor(b);
    });

    // Country tab split: a company has one country, so key off the first row.
    return sorted.filter((group) =>
      countryTab === "global" ? isGlobalRow(group[0]) : !isGlobalRow(group[0])
    );
  }, [pendingOrders, strategyFilter, searchTerm, selectedCategories, orderTypeFilter, priorityFilter, methodFilter, decisionFilter, lastOrderFilter, marketableFilter, rowTypeFilter, filterLevel, sortCol, sortDir, countryTab]);

  const filteredOmsRows = useMemo(() => {
    // "Orders only" hides the recommendation section entirely.
    if (rowTypeFilter === "orders") return [];

    // Pending-order-level filters (type / priority / decision / last order /
    // marketability) describe tracker rows only — recommendations can never
    // match them, so hide the section while any of them is active.
    if (anyOrderLevelFilterActive(orderTypeFilter, priorityFilter, methodFilter, decisionFilter, lastOrderFilter, marketableFilter)) {
      return [];
    }

    // Companies that already have at least one tracker (pending order)
    const trackerCompanyIds = new Set(
      pendingOrders.filter((o) => !o.is_oms_rec).map((o) => o.company)
    );

    const hasSuggestion = (o: PendingOrder) => {
      const recs = o.recommendations ?? [];
      return (
        recs.some(
          (r) => (r.recommendation_type ?? "").toLowerCase() === "model" && hasActionableSuggestion(r.suggestions)
        ) ||
        // Both horizons: a five-year Analyst/IC rec drives LHF suggestions, a
        // three-year one drives Core, and either is reason enough to show the row.
        [3, 5].some((years) =>
          hasActionableSuggestion(findMatchedRec(recs, o.latest_rec_analyst_name_oms, years)?.suggestions) ||
          hasActionableSuggestion(findMatchedRec(recs, o.latest_rec_ic_user_name_oms, years)?.suggestions)
        )
      );
    };

    // Only real OMS rows from backend for companies with NO trackers.
    // Companies with trackers are handled via injection in groupedByCompany.
    // By design we surface ONLY rows with a triggered suggestion (hasSuggestion):
    // a row whose recommendations carry no actionable trade_type — including the
    // empty/null recommendations case — is intentionally hidden, not shown blank.
    const rows = pendingOrders.filter(
      (o) => o.is_oms_rec && !o.is_placeholder && hasSuggestion(o) && !hasCurrencyMismatch(o) && matchesFilters(o, strategyFilter, searchTerm, selectedCategories, orderTypeFilter, priorityFilter, methodFilter, decisionFilter, lastOrderFilter, marketableFilter) && !trackerCompanyIds.has(o.company)
        && (countryTab === "global" ? isGlobalRow(o) : !isGlobalRow(o))
    );

    if (sortCol) {
      rows.sort((a, b) => {
        const cmp =
          sortCol === "company" ? (a.company_name ?? "").localeCompare(b.company_name ?? "")
          : sortCol === "days"  ? (a.days ?? Infinity) - (b.days ?? Infinity)
          : decisionRank(a.cio_decision) - decisionRank(b.cio_decision);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    return rows;
  }, [pendingOrders, strategyFilter, searchTerm, selectedCategories, orderTypeFilter, priorityFilter, methodFilter, decisionFilter, lastOrderFilter, marketableFilter, rowTypeFilter, sortCol, sortDir, countryTab]);

  // Counted from raw pendingOrders, not the filter-bar-filtered groupedByCompany, and by
  // tracker not company — the action covers the whole tab and the backend returns rows.
  const prevDayContinueCount = useMemo(() => {
    let count = 0;
    for (const order of pendingOrders) {
      if (order.is_oms_rec || !order.is_prev_day_continue || order.cio_decision != null) continue;
      if (isGlobalRow(order) !== (countryTab === "global")) continue;
      count += 1;
    }
    return count;
  }, [pendingOrders, countryTab]);

  // Gates the Domestic sign-off. Counted off raw pendingOrders like prevDayContinueCount
  // above — the filter bar narrows what the user sees, not what the CIO decided.
  const todayContinueCount = useMemo(() => {
    let count = 0;
    for (const order of pendingOrders) {
      if (order.is_oms_rec || order.cio_decision !== "continue") continue;
      if (isGlobalRow(order) !== (countryTab === "global")) continue;
      count += 1;
    }
    return count;
  }, [pendingOrders, countryTab]);

  // Its own flag, not the page-level `loading`: that one dims the table and disables
  // the edit buttons, and this request loads nothing.
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);

  const markDomesticComplete = useCallback(async (): Promise<MarkCompleteResult> => {
    try {
      setIsMarkingComplete(true);
      const { notified } = await orderRecommendationApi.markDomesticComplete();
      if (!notified) return "nothing-to-send";
      setSnackbar({
        open: true,
        message: "Trade desk notified — today's instructions have been set",
        severity: "success",
      });
      // No fetchPendingOrders(): this action changes no row.
      return "notified";
    } catch {
      setSnackbar({ open: true, message: "Failed to notify the trade desk", severity: "error" });
      return "error";
    } finally {
      setIsMarkingComplete(false);
    }
  }, []);

  const continuePrevDayOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { continued } = await orderRecommendationApi.continuePrevDayOrders(countryTab);
      setSnackbar({
        open: true,
        message: continued > 0
          ? `Marked ${continued} ${continued === 1 ? "order" : "orders"} as Continue`
          : "No previous day Continue orders to carry forward",
        // Zero updated is a benign race, not a failure — "error" stays for the catch.
        severity: continued > 0 ? "success" : "info",
      });
      await fetchPendingOrders();
    } catch {
      setSnackbar({ open: true, message: "Failed to continue previous day orders", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [countryTab, fetchPendingOrders]);

  const toggleCategory = useCallback((cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
    // setSelectedCategories is tab-scoped: its identity changes with countryTab,
    // so it must be a dependency or toggling would target the wrong tab's slice.
  }, [setSelectedCategories]);

  const startBulkEdit = useCallback(() => {
    const initialEdits: Record<number, BulkEditFields> = {};
    pendingOrders.forEach((o) => {
      if (!o.is_oms_rec) {
        const execType = o.execution_type ?? "limit";
        // Seed limit_price with 1.02*CMP when limit order has no saved price yet.
        const seededPrice =
          execType === "limit" && o.limit_price == null
            ? calcDefaultPrice(o.latest_price)
            : o.limit_price != null ? String(o.limit_price) : "";
        initialEdits[o.id] = {
          limit_price: seededPrice,
          target_holding_pct: o.target_holding_pct || "",
          cio_target_pct: o.cio_target_pct || "",
          cio_decision: o.cio_decision || null,
          comment: o.comment || "",
          execution_type: execType,
          execution_method: o.execution_method ?? "pct_volume",
          volume_pct: o.volume_pct != null ? String(o.volume_pct) : "5",
          priority: o.priority ?? "low",
        };
      }
    });
    setBulkEditState(initialEdits);
    setBulkEditSnapshot(initialEdits);
    setBulkCreatePair({ state: {}, snapshot: {} });
    savedCreateKeysRef.current = new Set();
    partiallySavedRef.current = false;
    setSaveErrors({ createKeys: [], editIds: [] });
    setIsBulkEditing(true);
  }, [pendingOrders]);

  // Single teardown for leaving edit mode: clears every in-flight session holder
  // and optionally refetches. Both Cancel and a clean save-exit go through here so
  // neither path can forget to reset one of them. Pass refetch=true when an earlier
  // partial save left pendingOrders stale (newly-created orders are still only
  // synthetic placeholders) so read mode shows the rows that were persisted.
  const exitBulkEdit = useCallback((refetch: boolean): Promise<void> => {
    setIsBulkEditing(false);
    setBulkEditState({});
    setBulkEditSnapshot({});
    setBulkCreatePair({ state: {}, snapshot: {} });
    setSaveErrors({ createKeys: [], editIds: [] });
    savedCreateKeysRef.current = new Set();
    partiallySavedRef.current = false;
    return refetch ? fetchPendingOrders() : Promise.resolve();
  }, [fetchPendingOrders]);

  const cancelBulkEdit = useCallback(() => {
    void exitBulkEdit(partiallySavedRef.current);
  }, [exitBulkEdit]);

  const handleBulkEditChange = useCallback(
    (id: number, field: keyof BulkEditFields, value: BulkEditFields[keyof BulkEditFields]) => {
      setBulkEditState((prev) => {
        const current = prev[id];
        if (!current) return prev;

        const nextFields = { ...current, [field]: value };

        // If switching to 'limit' and price is empty, auto-fill it. A market order is
        // seeded with "" rather than null, so test for blank, not just missing.
        if (field === "execution_type" && value === "limit" && !current.limit_price) {
          const order = pendingOrders.find((o) => o.id === id);
          nextFields.limit_price = calcDefaultPrice(order?.latest_price);
        }

        return {
          ...prev,
          [id]: nextFields,
        };
      });
    },
    [pendingOrders]
  );

  const handleBulkCreateChange = useCallback(
    (companyId: number, strategyGroup: "LHF" | "CORE", field: keyof BulkCreateFields, value: BulkCreateFields[keyof BulkCreateFields]) => {
      const key = `${companyId}_${strategyGroup}`;
      // Capture CMP before entering the setter (pendingOrders is stable here).
      const cmp = pendingOrders.find((o) => o.company === companyId)?.latest_price ?? null;
      // Seed limit_price with 1.02*CMP when this create row is first touched. Computed
      // once here (not inside the state setter) so the same object seeds both
      // state and snapshot on first touch — the snapshot is what "dirty" field
      // highlighting diffs later drafts against.
      const defaults: BulkCreateFields = {
        order_type: null,
        limit_price: calcDefaultPrice(cmp),
        target_holding_pct: "",
        cio_target_pct: "",
        cio_decision: null,
        comment: "",
        execution_type: "limit",
        execution_method: "pct_volume",
        volume_pct: "5",
        priority: "low",
      };

      // state and snapshot update in a single setState call so a render never
      // observes one updated without the other.
      setBulkCreatePair((prev) => {
        const existing = prev.state[key] || defaults;
        const nextFields = { ...existing, [field]: value };

        // If switching to 'limit' and price is empty, auto-fill it.
        if (field === "execution_type" && value === "limit" && !existing.limit_price) {
          nextFields.limit_price = calcDefaultPrice(cmp);
        }

        return {
          state: { ...prev.state, [key]: nextFields },
          snapshot: prev.snapshot[key] ? prev.snapshot : { ...prev.snapshot, [key]: defaults },
        };
      });
    },
    [pendingOrders]
  );

  const saveBulkEdits = useCallback(async () => {
    // ── Pre-save validation (hard blocks only) ────────────────────────────
    const validationErrors: string[] = [];
    const errorEditIds: number[] = [];
    const errorCreateKeys: string[] = [];

    Object.entries(bulkEditState).forEach(([idStr, fields]) => {
      const order = pendingOrders.find((o) => o.id === parseInt(idStr));
      if (!order) return;
      const rowLabel = `${order.company_name} (${order.strategy_group ?? order.strategy_code ?? "—"})`;
      if (fields.execution_method === "pct_volume") {
        const pct = parseFloat(fields.volume_pct);
        if (!fields.volume_pct || isNaN(pct) || pct <= 0 || pct > 100) {
          validationErrors.push(`${rowLabel}: enter a Volume % between 1 and 100`);
          errorEditIds.push(order.id);
        }
      }
      // Continue requires a CIO target position. Only changed rows are checked —
      // unchanged rows are not part of this save and must not block it.
      const snapshot = bulkEditSnapshot[order.id];
      const changed =
        !snapshot ||
        (Object.keys(fields) as (keyof BulkEditFields)[]).some((k) => fields[k] !== snapshot[k]);
      if (
        changed &&
        fields.cio_decision === "continue" &&
        (fields.cio_target_pct ?? "").trim() === ""
      ) {
        validationErrors.push(`${rowLabel}: enter a CIO target position for this "Continue" order`);
        if (!errorEditIds.includes(order.id)) errorEditIds.push(order.id);
      }
      // The backend accepts a null price, so a priceless Limit order would save silently.
      if (changed && fields.execution_type === "limit" && (fields.limit_price ?? "").trim() === "") {
        validationErrors.push(`${rowLabel}: enter a limit price for this Limit order`);
        if (!errorEditIds.includes(order.id)) errorEditIds.push(order.id);
      }
    });

    Object.entries(bulkCreateState).forEach(([key, fields]) => {
      const [companyIdStr, strategyGroup] = key.split("_");
      const companyName =
        pendingOrders.find((o) => o.company === parseInt(companyIdStr))?.company_name ??
        `Company #${companyIdStr}`;

      // A CIO position (or comment) was entered but the Buy/Sell decision was
      // left blank (e.g. reverted to "—"). Without a decision the row would be
      // silently dropped on save, so block it here instead.
      const hasData =
        (fields.cio_target_pct ?? "").trim() !== "" || (fields.comment ?? "").trim() !== "";
      if (fields.order_type === null) {
        if (hasData) {
          validationErrors.push(
            `${companyName} (${strategyGroup}): choose Buy or Sell — this entry can't be saved without it`
          );
          errorCreateKeys.push(key);
        }
        return;
      }

      if (fields.execution_method === "pct_volume") {
        const pct = parseFloat(fields.volume_pct);
        if (!fields.volume_pct || isNaN(pct) || pct <= 0 || pct > 100) {
          validationErrors.push(`${companyName} (${strategyGroup}): enter a Volume % between 1 and 100`);
          if (!errorCreateKeys.includes(key)) errorCreateKeys.push(key);
        }
      }

      // New rows default to Continue on save — they too need a CIO target.
      const effectiveDecision = fields.cio_decision ?? "continue";
      if (effectiveDecision === "continue" && (fields.cio_target_pct ?? "").trim() === "") {
        validationErrors.push(
          `${companyName} (${strategyGroup}): enter a CIO target position for this order`
        );
        if (!errorCreateKeys.includes(key)) errorCreateKeys.push(key);
      }

      if (fields.execution_type === "limit" && (fields.limit_price ?? "").trim() === "") {
        validationErrors.push(`${companyName} (${strategyGroup}): enter a limit price for this Limit order`);
        if (!errorCreateKeys.includes(key)) errorCreateKeys.push(key);
      }
    });

    setSaveErrors({ createKeys: errorCreateKeys, editIds: errorEditIds });
    const errorEditIdSet = new Set(errorEditIds);
    const hasErrors = validationErrors.length > 0;
    // Snackbar text: a lead sentence followed by the bulleted list of failing rows.
    const validationMessage = (lead: string) =>
      `${lead} attention:\n• ${validationErrors.join("\n• ")}`;
    // ── End validation ────────────────────────────────────────────────────

    // Captured off the payload built below so the catch can name the rows the server
    // rejected: bulk-save answers 400 with field errors keyed "updates.<i>"/"creates.<i>",
    // and the index alone means nothing to the user.
    let sentUpdateIds: number[] = [];
    let sentCreateKeys: string[] = [];

    try {
      setLoading(true);

      // Helper: keep explicit blank as null; only default when the value is actually missing.
      const resolvePrice = (
        limitPrice: string | null | undefined,
        cmp: number | null | undefined
      ): string | null => {
        if (limitPrice == null) {
          return calcDefaultPrice(cmp);
        }
        const trimmed = String(limitPrice).trim();
        if (trimmed === "") {
          return null;
        }
        return trimmed;
      };

      // Orders marked "completed" skip the field update and are sent as a completion
      // instead, but still ride in the same bulk-save request — one save is one request,
      // and so one Google Chat notification rather than one per completed row.
      // Error rows are excluded from all save paths and remain in edit mode for the user to fix.
      const completedOrderIds = Object.entries(bulkEditState)
        .filter(([idStr, fields]) => fields.cio_decision === "completed" && !errorEditIdSet.has(parseInt(idStr)))
        .map(([idStr]) => parseInt(idStr));
      const completedOrderIdSet = new Set(completedOrderIds);

      const fieldUpdates = Object.entries(bulkEditState)
        .filter(([idStr, fields]) => {
          if (errorEditIdSet.has(parseInt(idStr))) return false;
          if (fields.cio_decision === "completed") return false;
          const snapshot = bulkEditSnapshot[parseInt(idStr)];
          if (!snapshot) return false;
          return (Object.keys(fields) as (keyof BulkEditFields)[]).some(k => fields[k] !== snapshot[k]);
        })
        .map(([idStr, fields]) => {
          const order = pendingOrders.find((o) => o.id === parseInt(idStr))!;
          return {
            id: parseInt(idStr),
            ...fields,
            limit_price:
              fields.execution_type === "limit"
                ? resolvePrice(fields.limit_price, order.latest_price)
                : fields.limit_price,
          };
        });

      // Completions carry only the flag: the row is leaving the list, so any other edit
      // made to it in this session is deliberately not saved (unchanged behaviour).
      const updates = [
        ...fieldUpdates,
        ...completedOrderIds.map((id) => ({ id, is_completed: true })),
      ];
      sentUpdateIds = updates.map((u) => u.id);

      // Exclude error rows (not yet valid) and rows already persisted earlier in
      // this session (kept in state only so the row keeps showing its buy/sell).
      const createEntries = Object.entries(bulkCreateState).filter(
        ([key, fields]) =>
          fields.order_type !== null &&
          !errorCreateKeys.includes(key) &&
          !savedCreateKeysRef.current.has(key)
      );
      const creates = createEntries.map(([key, fields]) => {
        const [companyIdStr, strategyGroup] = key.split("_");
        const companyId = parseInt(companyIdStr);
        const cmp = pendingOrders.find((o) => o.company === companyId)?.latest_price;
        return {
          company: companyId,
          strategy_group: strategyGroup as "LHF" | "CORE",
          ...fields,
          order_type: fields.order_type as 1 | 2,
          limit_price:
            fields.execution_type === "limit"
              ? resolvePrice(fields.limit_price, cmp)
              : fields.limit_price,
          cio_decision: fields.cio_decision ?? "continue",
        };
      });
      sentCreateKeys = createEntries.map(([key]) => key);

      if (updates.length === 0 && creates.length === 0) {
        if (hasErrors) {
          setSnackbar({
            open: true,
            message: validationMessage(`No rows were saved — ${validationErrors.length === 1 ? "one row needs" : "all rows need"}`),
            severity: "error",
          });
        } else {
          // An earlier partial save left pendingOrders stale; leave through the teardown so it refetches.
          await exitBulkEdit(partiallySavedRef.current);
        }
        return;
      }

      if (updates.length > 0 || creates.length > 0) {
        await orderRecommendationApi.bulkSaveOrders({ updates, creates });
        // These create rows now exist server-side; never send them again this session.
        createEntries.forEach(([key]) => savedCreateKeysRef.current.add(key));
      }

      if (completedOrderIds.length > 0) {
        setPendingOrders((prev) => prev.filter((o) => !completedOrderIdSet.has(o.id)));
      }

      if (hasErrors) {
        // We saved rows but stay in edit mode, so pendingOrders is now stale —
        // flag it so exiting edit mode refetches the true server state.
        partiallySavedRef.current = true;
        setSnackbar({
          open: true,
          message: validationMessage(`Some rows were saved. ${validationErrors.length === 1 ? "One row needs" : "A few rows need"}`),
          severity: "warning",
        });
        // Keep all drafts so saved rows keep showing their values in edit mode.
        // - Completed rows are gone from the table, so drop them from edit state.
        // - Advance the snapshot for saved edit rows so they aren't re-sent next save.
        // - bulkCreateState stays intact; savedCreateKeysRef stops saved creates re-sending.
        // Both setters intentionally forget completed rows (state drops them, snapshot
        // deletes their key) — the double-removal keeps the two maps consistent.
        // bulkEditState read inside the snapshot updater is the draft captured for this
        // save (it's a useCallback dep), so it's stable across these batched setters.
        if (completedOrderIds.length > 0) {
          setBulkEditState(prev =>
            Object.fromEntries(Object.entries(prev).filter(([idStr]) => !completedOrderIdSet.has(parseInt(idStr))))
          );
        }
        setBulkEditSnapshot(prev => {
          const next = { ...prev };
          Object.keys(bulkEditState).forEach(idStr => {
            const id = parseInt(idStr);
            if (completedOrderIdSet.has(id)) { delete next[id]; return; }
            if (!errorEditIdSet.has(id)) next[id] = bulkEditState[id];
          });
          return next;
        });
      } else {
        setSnackbar({ open: true, message: "Changes saved successfully", severity: "success" });
        // Refetch on exit when this save (or an earlier partial save) changed data,
        // so read mode reflects it — even if this final save only completed rows.
        await exitBulkEdit(updates.length > 0 || creates.length > 0 || partiallySavedRef.current);
      }
    } catch (err) {
      console.error(err);
      // bulk-save rejects the whole request on one bad field and answers 400 with
      // {"updates.<i>": {"limit_price": ["..."]}}. Report which row and which field —
      // a bare "failed to save" leaves nothing to act on. Rows the server named are
      // flagged alongside any that pre-save validation already caught.
      const body = isAxiosError(err) ? err.response?.data : null;
      const rejectedEditIds: number[] = [];
      const rejectedCreateKeys: string[] = [];
      const lines: string[] = [];

      if (body && typeof body === "object" && !Array.isArray(body)) {
        Object.entries(body as Record<string, unknown>).forEach(([key, fieldErrors]) => {
          const [kind, indexStr] = key.split(".");
          const index = parseInt(indexStr);
          let label = "";

          if (kind === "updates" && !isNaN(index)) {
            const id = sentUpdateIds[index];
            const order = pendingOrders.find((o) => o.id === id);
            if (id != null) rejectedEditIds.push(id);
            if (order) label = `${order.company_name} (${order.strategy_group ?? order.strategy_code ?? "—"})`;
          } else if (kind === "creates" && !isNaN(index)) {
            const createKey = sentCreateKeys[index];
            if (createKey != null) {
              rejectedCreateKeys.push(createKey);
              const [companyIdStr, group] = createKey.split("_");
              const order = pendingOrders.find((o) => o.company === parseInt(companyIdStr));
              label = `${order?.company_name ?? `Company ${companyIdStr}`} (${group}, new)`;
            }
          }

          const detail =
            fieldErrors && typeof fieldErrors === "object"
              ? Object.entries(fieldErrors as Record<string, string | string[]>)
                  .map(([field, msgs]) => `${field.replace(/_/g, " ")} — ${[msgs].flat().join(" ")}`)
                  .join("; ")
              : String(fieldErrors);
          // A key naming no row (a DRF-level "detail", say) reads as a broken row
          // reference if it keeps its prefix — show it as a plain sentence instead.
          lines.push(label ? `${label}: ${detail}` : detail);
        });
      }

      if (rejectedEditIds.length > 0 || rejectedCreateKeys.length > 0) {
        setSaveErrors((prev) => ({
          createKeys: Array.from(new Set([...prev.createKeys, ...rejectedCreateKeys])),
          editIds: Array.from(new Set([...prev.editIds, ...rejectedEditIds])),
        }));
      }
      setSnackbar({
        open: true,
        message: lines.length
          ? `Nothing was saved — the server rejected:\n• ${lines.join("\n• ")}`
          : "Failed to save changes",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [bulkEditState, bulkEditSnapshot, bulkCreateState, pendingOrders, exitBulkEdit]);

  const handleConfirmAction = useCallback(async () => {
    const { type, orderId } = confirmDialog;
    if (!orderId) {
      setConfirmDialog({ open: false, type: null, orderId: null, companyName: null });
      return;
    }
    try {
      const orderToDelete = pendingOrders.find(o => o.id === orderId);
      const companyId = orderToDelete?.company;

      if (type === "delete") await orderRecommendationApi.deleteOrder(orderId);
      else if (type === "complete")
        await orderRecommendationApi.updateOrder(orderId, { is_completed: true });
      
      setPendingOrders((prev) => {
        const updated = prev.filter((o) => o.id !== orderId);
        // If this was the last tracker for this company, reload to ensure
        // recommendations are correctly synchronized and categorized.
        const remainingForCompany = updated.filter(o => !o.is_oms_rec && o.company === companyId);
        if (remainingForCompany.length === 0) {
          void fetchPendingOrders();
        }
        return updated;
      });
    } catch {
      setSnackbar({ open: true, message: `Failed to ${type} order`, severity: "error" });
      await fetchPendingOrders();
    } finally {
      setConfirmDialog({ open: false, type: null, orderId: null, companyName: null });
    }
  }, [confirmDialog, fetchPendingOrders, pendingOrders]);

  const closeConfirmDialog = useCallback(
    () => setConfirmDialog({ open: false, type: null, orderId: null, companyName: null }),
    []
  );

  const closeSnackbar = useCallback(() => setSnackbar((p) => ({ ...p, open: false })), []);

  // CSV export — all real pending orders (recommendation rows excluded).
  const exportOrdersCsv = useCallback(() => {
    // The active country tab's real orders only — a Domestic export never carries Global rows.
    const rows = pendingOrders.filter(
      (o) => !o.is_oms_rec && !o.is_placeholder && isGlobalRow(o) === (countryTab === "global")
    );
    if (rows.length === 0) {
      setSnackbar({ open: true, message: `No ${countryTab === "global" ? "Global" : "Domestic"} orders to export`, severity: "error" });
      return;
    }

    // Column sequence mirrors the on-screen table: identity → order →
    // execution details → market data → decision → holdings → CIO target → comment.
    const headers = [
      "Company", "Symbol", "Category", "Strategy", "Order Type",
      "Execution", "Limit Price", "Volume %", "Priority",
      "CMP", "Days to Trade 1% Position",
      "Decision", "Holding % (PMS/LHF)", "Holding % (AIF/LHF-AIF)", "Holding % (IFSC)",
      "CIO Target %",
      "Comment",
    ];
    const esc = (v: string | number | null | undefined): string => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // Same display rule as the table's Holding % cells: show only positive values.
    const holdingPct = (v: number | null | undefined): string =>
      v != null && v > 0 ? v.toFixed(2) : "";
    const lines = rows.map((o) => {
      // Sub-strategy holdings — same per-row selection as TableRow:
      // LHF rows read LHF / LHF_AIF; CORE rows read PMS / AIF / IFSC.
      const isLhfRow = o.strategy_group === "LHF" || LHF_CODES.has(o.strategy_code ?? "");
      const hft = o.holdings_by_firm_type ?? {};
      return [
        o.company_name,
        o.company_symbol,
        o.company_category ?? "",
        o.strategy_group ?? o.strategy_code ?? "",
        o.order_type === 1 ? "Buy" : "Sell",
        o.execution_type ?? "limit",
        o.execution_type === "market" ? "" : o.limit_price ?? "",
        o.volume_pct ?? "",
        o.priority ?? "low",
        o.latest_price ?? "",
        o.days ?? "",
        o.cio_decision === "continue" ? "Continue" : o.cio_decision === "completed" ? "Completed" : "",
        holdingPct(isLhfRow ? hft.LHF : hft.PMS),
        holdingPct(isLhfRow ? hft.LHF_AIF : hft.AIF),
        holdingPct(isLhfRow ? null : hft.IFSC_AIF),
        o.cio_target_pct ?? "",
        o.comment ?? "",
      ].map(esc).join(",");
    });

    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${countryTab}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [pendingOrders, countryTab]);

  // Patches every row of a company after its note is saved, so the icon updates without
  // refetching the whole list.
  const applyCioNote = useCallback((companyId: number, cioNote: string | null) => {
    setPendingOrders(prevOrders =>
      prevOrders.map(order =>
        order.company === companyId ? { ...order, cio_note: cioNote } : order
      )
    );
  }, []);

  return {
    pendingOrders,
    fetchPendingOrders,
    applyCioNote,
    loading,
    error,
    recStartDate,
    setRecStartDate,
    strategyFilter,
    setStrategyFilter,
    selectedCategories,
    setSelectedCategories,
    defaultCategories,
    searchTerm,
    setSearchTerm,
    orderTypeFilter,
    setOrderTypeFilter,
    priorityFilter,
    setPriorityFilter,
    methodFilter,
    setMethodFilter,
    decisionFilter,
    setDecisionFilter,
    lastOrderFilter,
    setLastOrderFilter,
    marketableFilter,
    setMarketableFilter,
    rowTypeFilter,
    setRowTypeFilter,
    filterLevel,
    setFilterLevel,
    countryTab,
    setCountryTab,
    isBulkEditing,
    bulkEditState,
    bulkEditSnapshot,
    bulkCreateState,
    bulkCreateSnapshot,
    startBulkEdit,
    cancelBulkEdit,
    handleBulkEditChange,
    handleBulkCreateChange,
    saveBulkEdits,
    saveErrors,
    confirmDialog,
    setConfirmDialog,
    snackbar,
    setSnackbar,
    toggleCategory,
    handleConfirmAction,
    closeConfirmDialog,
    closeSnackbar,
    groupedByCompany,
    filteredOmsRows,
    sortCol,
    sortDir,
    toggleSort,
    exportOrdersCsv,
    riskByCompanyId,
    companiesMissingLiquidity,
    prevDayContinueCount,
    continuePrevDayOrders,
    todayContinueCount,
    markDomesticComplete,
    isMarkingComplete,
  };
};
